'use client';

/**
 * Shared heading components that render the correct `h{n}` tag based on a
 * surrounding context, so nested sections don't have to hardcode their level.
 */

import React, {createContext, useContext} from 'react';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const HeadingLevelContext = createContext<number>(2);

export function HeadingLevelProvider({
    level,
    children,
}: {
    level: number;
    children: React.ReactNode;
}) {
    return <HeadingLevelContext.Provider value={level}>{children}</HeadingLevelContext.Provider>;
}

function useHeadingLevel() {
    const level = useContext(HeadingLevelContext);
    const Heading = `h${Math.min(level, 6)}` as HeadingTag;
    const SubHeading = `h${Math.min(level + 1, 6)}` as HeadingTag;
    return {Heading, SubHeading, level};
}

/** Renders `h{level}` from the surrounding `<HeadingLevelProvider>`. */
export const Heading: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...rest
}) => {
    const {Heading: HeadingTag} = useHeadingLevel();
    return <HeadingTag {...rest}>{children}</HeadingTag>;
};

/** Renders `h{level + 1}` from the surrounding `<HeadingLevelProvider>`. */
export const SubHeading: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
    children,
    ...rest
}) => {
    const {SubHeading: HeadingTag} = useHeadingLevel();
    return <HeadingTag {...rest}>{children}</HeadingTag>;
};
